// A game page's chart panel as it's first written, before app.js draws the
// series: its stats, range tabs, Compare button and the toggles for what's
// laid over the chart (update markers, sales, evenings); and the play-time
// insight, which shades evenings on it. Nothing here touches the DOM.

import { type AppResponse, HISTORY_RANGES, type HistoryRange } from "../../shared/api.ts";
import { EVENINGS, REGIONS, type Region, playTimes } from "../../shared/evenings.ts";
import { clock, date, number } from "./figures.ts";
import { displayName, stat } from "./markup.ts";
import { compareNeighbours, compareWith } from "./routes.ts";
import { kindToggles } from "./updates.ts";
import { escapeHtml } from "./web.ts";

// The chart's ranges drawn hourly, the only ones evenings are shaded on, an
// evening being a few hours of a day.
export const HOURLY_RANGES: ReadonlySet<string> = new Set<HistoryRange>(["24h", "7d", "30d"]);

// The play-time insight's hint (see playTimeCard), by whether the range shown
// is hourly, the only kind evenings are shaded on.
export const playHint = (hourly: boolean): string =>
  hourly ? "click to shade on the chart" : "switch to 24h, 7d or 30d to shade it on the chart";

// One of the chart's stats as stats() takes it: its value and its note.
export type StatText = [value: string, note?: string];

// The game chart's Compare button, beside its range tabs (app.js keeps its
// range in step with theirs), naming the games it adds: in its text, for
// screen readers and on phones, where it has a row of its own (style.css),
// and in its tip on wider screens. Nothing for a game with no ranked
// neighbours (not ranked, or no longer tracked): Compare would show it alone.
// Its icon is two lines in the compare page's first two colours.
export function compareButton(game: AppResponse, name: string, range: HistoryRange): string {
  const others = compareNeighbours(game).map((row) => displayName(row));
  if (!others.length) return "";
  const what = `${name} with ${others.join(" and ")}`;

  return `<a class="compare-link" href="${compareWith(game, range)}" data-nav rel="nofollow"
      title="${escapeHtml(`Compare ${what} on one chart`)}">
      <svg viewBox="0 0 16 16" aria-hidden="true"><path class="s0" d="M1 11.5l4-4.5 3 2.5 7-6.5"/>
        <path class="s1" d="M1 14l4-2.5 3 1 7-4"/></svg>Compare<span class="compare-what">
        ${escapeHtml(what)}</span></a>`;
}

// The chart's stats: the latest count and all-time peak, from the app
// itself so they show before the history arrives and stay put as ranges
// change, then the range's high and low. Those are always there, as "—"
// until app.js has the series (`rangeStats`: { high, low }, each
// [value, note]), so the set of stats, and so the layout around them,
// doesn't change with the data.
export function stats(
  game: AppResponse,
  range: HistoryRange,
  rangeStats: { high: StatText; low: StatText } | null = null
): string {
  let html = stat(
    "Latest",
    game.player_count == null ? "—" : number.format(game.player_count)
  );

  if (game.peak_player_count != null) {
    html += stat(
      "Peak · all time",
      number.format(game.peak_player_count),
      game.peak_recorded_at == null ? "" : date(game.peak_recorded_at)
    );
  }

  return (
    html +
    stat(`High · ${range}`, ...(rangeStats?.high ?? ["—"])) +
    stat(`Low · ${range}`, ...(rangeStats?.low ?? ["—"]))
  );
}

// The chart's panel before its series arrives: the stats, the range tabs,
// an empty chart, and its toggles for update markers (`shownKinds`, a Set),
// sales (pressed when `showSales`) and, on hourly ranges, evenings
// (pressed where `isShaded`). app.js fills
// it in; the Worker writes it as it is, so the page is laid out the same
// before and after app.js runs.
export function chartPanel(
  game: AppResponse,
  name: string,
  {
    range,
    shownKinds,
    showSales,
    isShaded
  }: {
    range: HistoryRange;
    shownKinds: ReadonlySet<string>;
    showSales: boolean;
    isShaded: (region: Region) => boolean;
  }
): string {
  return `
      <section class="panel chart-panel">
        <div class="chart-top">
          <div class="stats">${stats(game, range)}</div>
          <div class="chart-actions">
            <div class="range-tabs" role="group" aria-label="Time range">
              ${HISTORY_RANGES.map((r) => `<button type="button" data-range="${r}">${r}</button>`).join("")}
            </div>
            ${compareButton(game, name, range)}
          </div>
        </div>
        <div class="chart"><div class="status">Loading…</div></div>
        <div class="chart-legend">
          <div class="toggles update-toggles" role="group" aria-label="Update markers">
            <span class="muted">Updates</span>
            ${kindToggles("data-update-kind", shownKinds, true)}
          </div>
          <div class="toggles sale-toggles" role="group" aria-label="Sales shaded">
            <span class="muted">Prices</span>
            <button type="button" class="sale" data-sales aria-pressed="${showSales}"
              >sales <span class="count"></span></button>
          </div>
          <div class="toggles evening-toggles" role="group" aria-label="Evenings shaded"
               ${HOURLY_RANGES.has(range) ? "" : "hidden"}>
            <span class="muted">Evenings</span>
            ${REGIONS.map((region) => [region, EVENINGS[region]] as const)
            .map(
              ([region, { label }]) => `<button type="button" class="${region}"
                  data-evening="${region}" aria-pressed="${isShaded(region)}"
                  >${label}</button>`
            )
            .join("")}
          </div>
        </div>
      </section>`;
}

// When its players play: the evenings its tag names, each with its colour
// from the chart's shading, or "Around the clock", and its busiest hour.
// With evenings to shade, it's a toggle button for them (app.js sets
// whether it's pressed).
export function playTimeCard(game: AppResponse, range: HistoryRange): string {
  const { evenings, peakHour } = playTimes(game.tags);
  if (!evenings) return "";

  const value = evenings.length
    ? `${evenings.map((region) => EVENINGS[region].label).join(" & ")} evenings`
    : "Around the clock";
  const notes = [
    peakHour == null
      ? "Over the last 4 weeks"
      : `Busiest around ${clock(peakHour)} over the last 4 weeks`
  ];
  // Disabled, with a hint saying so, on ranges with no evenings to shade;
  // app.js keeps both in step as the range changes.
  const toggle = evenings.length > 0;
  const hourly = HOURLY_RANGES.has(range);
  if (toggle) notes.push(`<span class="play-hint">${playHint(hourly)}</span>`);

  return `
      <div class="insight play-time"
           ${toggle ? `role="button" tabindex="0" aria-pressed="false" aria-disabled="${!hourly}"` : ""}>
        <div class="stat-label">When it's played</div>
        <div class="insight-value">${
        evenings.length
          ? `<span class="swatches" aria-hidden="true">${evenings
              .map((region) => `<span class="swatch ${region}"></span>`)
              .join("")}</span>`
          : ""
      }<span>${value}</span></div>
        <small>${notes.join(" · ")}</small>
      </div>`;
}
